// ============================================================================
// LOCAL TENANT DATABASE IDENTITY
// ----------------------------------------------------------------------------
// PostgreSQL RLS is QuoteMaker's durable tenant boundary. IndexedDB is a local
// cache, but it survives sign-out, so it must not be shared by two authenticated
// tenants on the same browser origin.
//
// The JWT payload is sufficient to select a PENDING cache namespace, but it is
// not trusted to unlock that cache. App boot must verify /auth/me and call
// markLocalTenantSessionVerified() with the server-returned tenant before any
// local database read. Missing or invalid sessions bind to an empty quarantine
// namespace. The retired unscoped databases are never opened automatically.
// ============================================================================

export const LEGACY_LOCAL_DATABASE_NAME = 'quotemaker_v2';
const TENANT_DATABASE_PREFIX = 'quotemaker_v2::tenant::';
const QUARANTINE_DATABASE_PREFIX = 'quotemaker_v2::quarantine::';
const ACCESS_TOKEN_KEY = 'qm_access_token';
const REFRESH_TOKEN_KEY = 'qm_refresh_token';
const QUARANTINE_NONCE_KEY = 'qm_local_cache_quarantine_nonce';
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CANONICAL_POSTGRES_UUID_TEXT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCanonicalPostgresUuidText(value: unknown): value is string {
    return typeof value === 'string' && CANONICAL_POSTGRES_UUID_TEXT.test(value);
}

export type LocalDatabaseIdentity = {
    mode: 'tenant-pending-verification' | 'quarantine';
    databaseName: string;
    tenantId: string | null;
    userId: string | null;
};

export type VerifiedServerIdentity = {
    tenantId: unknown;
    userId: unknown;
};

type SessionStorageLike = Pick<Storage, 'getItem'> & Partial<Pick<Storage, 'setItem'>>;

let activeVerifiedTenantId: string | null = null;

function defaultSessionStorage(): SessionStorageLike | null {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    } catch {
        return null;
    }
}

function decodeBase64UrlJson(segment: string): Record<string, unknown> | null {
    try {
        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const decoded = atob(padded);
        const value = JSON.parse(decoded);
        return value && typeof value === 'object' && !Array.isArray(value)
            ? value as Record<string, unknown>
            : null;
    } catch {
        return null;
    }
}

export type AccessTokenIdentity = {
    tenantId: string;
    userId: string;
    expiresAt: number;
};

export function readIdentityFromAccessToken(token: unknown): AccessTokenIdentity | null {
    if (typeof token !== 'string' || !token.trim()) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const claims = decodeBase64UrlJson(parts[1]!);
    const tenantId = typeof claims?.tenantId === 'string' ? claims.tenantId.trim() : '';
    const userId = typeof claims?.userId === 'string' ? claims.userId.trim() : '';
    const expiresAt = Number(claims?.exp);
    if (!isCanonicalPostgresUuidText(tenantId)
        || !userId
        || !Number.isFinite(expiresAt)
        || expiresAt <= 0) return null;
    return { tenantId, userId, expiresAt };
}

export function readTenantIdFromAccessToken(token: unknown): string | null {
    return readIdentityFromAccessToken(token)?.tenantId || null;
}

function quarantineNonce(storage: SessionStorageLike | null): string {
    try {
        const existing = storage?.getItem(QUARANTINE_NONCE_KEY);
        if (existing && CANONICAL_UUID.test(existing)) return existing;
        const nonce = typeof globalThis.crypto?.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : '00000000-0000-4000-8000-000000000000';
        storage?.setItem?.(QUARANTINE_NONCE_KEY, nonce);
        return nonce;
    } catch {
        return '00000000-0000-4000-8000-000000000000';
    }
}

export function resolveLocalDatabaseIdentity(
    storage: SessionStorageLike | null = defaultSessionStorage(),
    nowMs = Date.now(),
): LocalDatabaseIdentity {
    let accessToken: string | null = null;
    let refreshToken: string | null = null;
    try {
        accessToken = storage?.getItem(ACCESS_TOKEN_KEY) || null;
        refreshToken = storage?.getItem(REFRESH_TOKEN_KEY) || null;
    } catch {
        accessToken = null;
        refreshToken = null;
    }

    const identity = readIdentityFromAccessToken(accessToken);
    const nowSeconds = Math.floor(Number(nowMs) / 1000);
    const accessIsCurrent = !!identity && identity.expiresAt > nowSeconds + 30;
    const accessCanRefresh = !!identity && !accessIsCurrent && !!refreshToken;
    if (identity && (accessIsCurrent || accessCanRefresh)) {
        return {
            mode: 'tenant-pending-verification',
            databaseName: `${TENANT_DATABASE_PREFIX}${identity.tenantId}`,
            tenantId: identity.tenantId,
            userId: identity.userId,
        };
    }

    return {
        mode: 'quarantine',
        databaseName: `${QUARANTINE_DATABASE_PREFIX}${quarantineNonce(storage)}`,
        tenantId: null,
        userId: null,
    };
}

/** Normal app boot never reads the retired unscoped database. */
export function shouldReadLegacyUnscopedDatabase(_identity: LocalDatabaseIdentity): boolean {
    return false;
}

export function markLocalTenantSessionVerified(
    serverIdentity: VerifiedServerIdentity,
    identity: LocalDatabaseIdentity,
): string {
    const verifiedTenantId = typeof serverIdentity?.tenantId === 'string'
        ? serverIdentity.tenantId.trim()
        : '';
    const verifiedUserId = typeof serverIdentity?.userId === 'string'
        ? serverIdentity.userId.trim()
        : '';
    const bootUserId = typeof identity.userId === 'string' ? identity.userId.trim() : '';
    if (!isCanonicalPostgresUuidText(verifiedTenantId)
        || !verifiedUserId
        || !bootUserId
        || identity.mode !== 'tenant-pending-verification'
        || identity.tenantId !== verifiedTenantId
        || bootUserId !== verifiedUserId) {
        const error: any = new Error('Authenticated identity does not match the local database binding.');
        error.code = 'LOCAL_TENANT_BINDING_MISMATCH';
        error.mismatchReason = !isCanonicalPostgresUuidText(verifiedTenantId)
            ? 'tenant-format-invalid'
            : !verifiedUserId
                ? 'server-user-missing'
                : !bootUserId
                    ? 'boot-user-missing'
                    : identity.mode !== 'tenant-pending-verification'
                        ? 'boot-mode-invalid'
                        : identity.tenantId !== verifiedTenantId
                            ? 'tenant-mismatch'
                            : 'user-mismatch';
        error.expectedTenantId = identity.tenantId;
        error.actualTenantId = verifiedTenantId || null;
        error.expectedUserId = identity.userId;
        error.actualUserId = verifiedUserId || null;
        throw error;
    }
    activeVerifiedTenantId = verifiedTenantId;
    return verifiedTenantId;
}

export function clearLocalTenantSessionVerification(): void {
    activeVerifiedTenantId = null;
}

export function getVerifiedLocalTenantId(): string | null {
    return activeVerifiedTenantId;
}

export function isLocalDatabaseIdentityVerified(
    identity: LocalDatabaseIdentity = resolveLocalDatabaseIdentity(),
): boolean {
    return identity.mode === 'tenant-pending-verification'
        && !!activeVerifiedTenantId
        && activeVerifiedTenantId === identity.tenantId;
}
